import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheckCircle, FiAlertTriangle, FiXCircle, FiDroplet } = FiIcons;

const MaterialCompatibility = () => {
  const [selectedMaterial, setSelectedMaterial] = useState('NBR');
  const [selectedMedium, setSelectedMedium] = useState(null);

  const materials = {
    NBR: {
      name: 'NBR (Nitril-Butadien-Kautschuk)',
      tempRange: '-30°C bis +100°C',
      description: 'Standard-Elastomer für Hydraulik und Pneumatik',
      shoreHardness: '70-90 Shore A',
      color: '#dc2626'
    },
    FKM: {
      name: 'FKM (Fluorkautschuk)',
      tempRange: '-20°C bis +200°C',
      description: 'Hochleistungs-Elastomer für extreme Bedingungen',
      shoreHardness: '75-90 Shore A',
      color: '#000000'
    },
    EPDM: {
      name: 'EPDM (Ethylen-Propylen-Dien)',
      tempRange: '-45°C bis +150°C',
      description: 'Witterungsbeständig, nicht ölbeständig',
      shoreHardness: '60-80 Shore A',
      color: '#7f1d1d'
    },
    VMQ: {
      name: 'VMQ (Silikon)',
      tempRange: '-60°C bis +200°C',
      description: 'Lebensmittelecht, physiologisch unbedenklich',
      shoreHardness: '40-80 Shore A',
      color: '#991b1b'
    }
  };

  const compatibilityMatrix = {
    NBR: {
      'Mineralöle': { rating: 'excellent', temp: 'bis +100°C' },
      'Hydrauliköle': { rating: 'excellent', temp: 'bis +100°C' },
      'Benzin': { rating: 'poor', temp: 'Quellen und Verhärtung' },
      'Wasser': { rating: 'good', temp: 'bis +80°C' },
      'Alkohol': { rating: 'good', temp: 'bis +60°C' },
      'Säuren (schwach)': { rating: 'fair', temp: 'begrenzt' },
      'Ozon': { rating: 'poor', temp: 'Rissbildung' },
      'UV-Strahlung': { rating: 'poor', temp: 'Alterung' }
    },
    FKM: {
      'Mineralöle': { rating: 'excellent', temp: 'bis +200°C' },
      'Hydrauliköle': { rating: 'excellent', temp: 'bis +200°C' },
      'Benzin': { rating: 'excellent', temp: 'bis +150°C' },
      'Säuren (stark)': { rating: 'excellent', temp: 'bis +150°C' },
      'Ketone': { rating: 'poor', temp: 'Quellen' },
      'Heißwasser': { rating: 'good', temp: 'bis +200°C' },
      'Ozon': { rating: 'excellent', temp: 'keine Alterung' },
      'UV-Strahlung': { rating: 'excellent', temp: 'stabil' }
    },
    EPDM: {
      'Mineralöle': { rating: 'poor', temp: 'Zerstörung!' },
      'Heißwasser': { rating: 'excellent', temp: 'bis +150°C' },
      'Dampf': { rating: 'excellent', temp: 'bis +150°C' },
      'Bremsflüssigkeit': { rating: 'excellent', temp: 'DOT 3/4' },
      'Ozon': { rating: 'excellent', temp: 'keine Alterung' },
      'UV-Strahlung': { rating: 'excellent', temp: 'stabil' },
      'Säuren (schwach)': { rating: 'good', temp: 'bis +100°C' },
      'Alkohol': { rating: 'excellent', temp: 'bis +100°C' }
    },
    VMQ: {
      'Lebensmittel': { rating: 'excellent', temp: 'FDA-konform' },
      'Heißluft': { rating: 'excellent', temp: 'bis +200°C' },
      'Mineralöle': { rating: 'fair', temp: 'Quellen möglich' },
      'Wasser': { rating: 'good', temp: 'bis +150°C' },
      'Säuren': { rating: 'good', temp: 'beständig' },
      'Benzin': { rating: 'poor', temp: 'Quellen' },
      'Ozon': { rating: 'excellent', temp: 'keine Alterung' },
      'UV-Strahlung': { rating: 'excellent', temp: 'stabil' }
    }
  };

  const getRatingInfo = (rating) => {
    switch (rating) {
      case 'excellent':
        return { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-100', text: 'Ausgezeichnet' };
      case 'good':
        return { icon: FiCheckCircle, color: 'text-blue-600', bg: 'bg-blue-100', text: 'Gut' };
      case 'fair':
        return { icon: FiAlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100', text: 'Bedingt' };
      case 'poor':
        return { icon: FiXCircle, color: 'text-red-600', bg: 'bg-red-100', text: 'Ungeeignet' };
      default:
        return { icon: FiDroplet, color: 'text-gray-600', bg: 'bg-gray-100', text: 'Unbekannt' };
    }
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiDroplet} className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Material-Kompatibilität</h2>
          <p className="text-gray-600">Beständigkeitsmatrix für Dichtungswerkstoffe</p>
        </div>
      </div>

      {/* Material Selection */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Werkstoff auswählen</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(materials).map(([key, material]) => (
            <button
              key={key}
              onClick={() => setSelectedMaterial(key)}
              className={`p-3 rounded-lg border text-left transition-all ${
                selectedMaterial === key
                  ? 'border-red-300 bg-red-50 ring-2 ring-red-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: material.color }}
                />
                <span className="font-medium text-sm">{key}</span>
              </div>
              <p className="text-xs text-gray-600">{material.tempRange}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Material Details */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-2">
          {materials[selectedMaterial].name}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Temperaturbereich:</span>
            <p className="font-medium">{materials[selectedMaterial].tempRange}</p>
          </div>
          <div>
            <span className="text-gray-600">Shore-Härte:</span>
            <p className="font-medium">{materials[selectedMaterial].shoreHardness}</p>
          </div>
          <div>
            <span className="text-gray-600">Beschreibung:</span>
            <p className="font-medium">{materials[selectedMaterial].description}</p>
          </div>
        </div>
      </div>

      {/* Compatibility Matrix */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">
          Medienbeständigkeit für {selectedMaterial}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(compatibilityMatrix[selectedMaterial] || {}).map(([medium, data]) => {
            const ratingInfo = getRatingInfo(data.rating);
            return (
              <motion.div
                key={medium}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedMedium === medium
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedMedium(selectedMedium === medium ? null : medium)}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{medium}</span>
                  <div className={`w-8 h-8 rounded-full ${ratingInfo.bg} flex items-center justify-center`}>
                    <SafeIcon icon={ratingInfo.icon} className={`w-4 h-4 ${ratingInfo.color}`} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={`font-medium ${ratingInfo.color}`}>
                    {ratingInfo.text}
                  </span>
                  <span className="text-gray-600">{data.temp}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900 mb-1">Sicherheitshinweis</h4>
            <p className="text-sm text-yellow-800">
              Die Angaben basieren auf Standardbedingungen (20°C, 1 bar). 
              Erhöhte Temperaturen, Drücke oder Medienkombinationen können 
              die Beständigkeit erheblich beeinflussen. Bei kritischen 
              Anwendungen sind Prüfungen unter realen Bedingungen erforderlich.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MaterialCompatibility;